import { Home, Refresh } from "@mui/icons-material";
import { Box, CircularProgress, Grid, IconButton, Tooltip } from "@mui/material";
import { COLORS } from "assets";
import { BoxSelect, FilterBar, SelectInput, SiteSelect } from "components";
import EventTypeFilter from "components/EventTypeFilter";
import { BREAK_GLASS_APPROVED_EVENT, ENDPOINTS, MODELS } from "config/constants";
import { activeTypeFilter, modelScope, threatsForModels } from "config/enabledModels";
import { useContextController } from "context";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { PrivateLayout } from "layouts";
import ThreatsTable from "pages/threats/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MILLISECONDS, ModelTypes, SiteModel, ThreatModel } from "types";
import NoDetectionModels from "components/NoDetectionModels";
import SuperBreakGlassPanel from "components/SuperBreakGlassPanel";
import { ensureBreakGlassForSite, isBreakGlassApproved, refreshBreakGlassApproved } from "utils";
import Bar from "./bar";
import CountChart from "./count";
import EmptyDashboardShell from "./EmptyDashboardShell";
import Pie from "./pie";

interface Data {
  threats?: ThreatModel[];
  totalRecords?: number;
}
const PERIODS = [
  { label: "Last 24 Hours", value: MILLISECONDS.DAY },
  { label: "Last Week", value: MILLISECONDS.WEEK },
  { label: "Last Month", value: MILLISECONDS.MONTH }
];

export default function Dashboard() {
  const t = useTranslation();
  const [{ userData }] = useContextController();
  const isSuper = userData?.type === "super";

  const [reload, setReload] = useState(false);
  const [period, setPeriod] = useState(PERIODS[0]);
  const [siteId, setSiteId] = useState<string>(() => localStorage.getItem("siteId") || "");
  const [sites, setSites] = useState<SiteModel[]>([]);
  const [boxId, setBoxId] = useState<string>();
  const [typeFilter, setTypeFilter] = useState("all");
  const { isLoading, response, callFetch } = useFetch<Data>("GET");
  const [pendingSiteId, setPendingSiteId] = useState<string | null>(null);
  const { isLoading: isLoadingOld, response: responseOld, callFetch: callFetchOld } = useFetch<Data>("GET");
  const loading = useMemo(() => isLoading || isLoadingOld, [isLoading, isLoadingOld]);
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const scope = useMemo(() => modelScope(sites, siteId ? [siteId] : []), [sites, siteId]);
  const enabledModels = scope.models;
  const selectedType = activeTypeFilter(enabledModels, typeFilter);
  const chartModels = useMemo<ModelTypes[]>(() => {
    if (selectedType === "all") return enabledModels;
    const picked = new Set(selectedType.split(","));
    return enabledModels.filter((model) => picked.has(model));
  }, [enabledModels, selectedType]);
  const chartThreats = useMemo(
    () => threatsForModels(response?.data?.threats, enabledModels, selectedType),
    [response?.data?.threats, enabledModels, selectedType]
  );
  useEffect(() => {
    if (activeTypeFilter(enabledModels, typeFilter) !== typeFilter) setTypeFilter("all");
  }, [enabledModels, typeFilter]);

  const applySite = useCallback((id: string) => {
    setSiteId(id);
    localStorage.setItem("siteId", id);
    localStorage.removeItem("boxId");
    setBoxId(undefined);
    setPendingSiteId(null);
  }, []);

  const clearSite = useCallback(() => {
    setSiteId("");
    localStorage.removeItem("siteId");
    localStorage.removeItem("boxId");
    setBoxId(undefined);
    setPendingSiteId(null);
  }, []);

  // Drop a stale siteId left in localStorage after logout (break-glass is session-only).
  useEffect(() => {
    if (!isSuper) return;
    refreshBreakGlassApproved(async (url) => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("break-glass list failed");
      return res.json();
    }).then((ids) => {
      const stored = localStorage.getItem("siteId");
      if (stored && !ids.includes(String(stored)) && !isBreakGlassApproved(stored)) {
        clearSite();
      }
    });
  }, [isSuper, clearSite]);

  useEffect(() => {
    if (!sites.length) return;
    if (siteId && sites.some((site) => site._id === siteId)) return;
    // Supers pick a site explicitly (and unlock via break-glass when needed).
    if (isSuper) return;
    const next = sites[0]._id;
    setSiteId(next);
    localStorage.setItem("siteId", next);
  }, [sites, siteId, isSuper]);

  useEffect(() => {
    const onApproved = (event: Event) => {
      const approved = (event as CustomEvent<{ siteId?: string }>).detail?.siteId;
      if (pendingSiteId && approved && String(approved) === String(pendingSiteId)) {
        applySite(pendingSiteId);
      } else if (approved && isSuper && !siteId) {
        applySite(String(approved));
      }
      onReload();
    };
    window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
    return () => window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
  }, [pendingSiteId, applySite, onReload, isSuper, siteId]);

  const needsSuperUnlock = isSuper && !siteId;

  useEffect(() => {
    if (!boxId) return;
    const old = new Date(Date.now() - period.value * 2);
    const from = new Date(Date.now() - period.value);
    const to = new Date(Date.now());
    const searchParams = new URLSearchParams();
    searchParams.append("boxId", boxId);
    searchParams.append("limit", "0");
    const params = searchParams.toString();
    const searchParamsNew = new URLSearchParams();
    searchParamsNew.append("from", from.toISOString());
    searchParamsNew.append("to", to.toISOString());
    const searchParamsOld = new URLSearchParams();
    searchParamsOld.append("from", old.toISOString());
    searchParamsOld.append("to", from.toISOString());
    callFetch(ENDPOINTS.threats + "?" + params + "&" + searchParamsNew.toString()).catch(console.error);
    callFetchOld(ENDPOINTS.threats + "?" + params + "&" + searchParamsOld.toString()).catch(console.error);
  }, [callFetch, callFetchOld, boxId, period, reload]);

  return (
    <PrivateLayout canAccess={() => true}>
      <Box mb={2.5} width="100%">
        <FilterBar
          light
          trailing={
            loading ? (
              <IconButton
                disabled
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  color: COLORS.surface.secondaryText,
                  border: `1px solid ${COLORS.surface.border}`,
                  bgcolor: COLORS.surface.card,
                  borderRadius: `${COLORS.radius.pill}px`
                }}
              >
                <CircularProgress size={18} color="inherit" />
              </IconButton>
            ) : (
              <Tooltip title={t["Refresh"]}>
                <IconButton
                  onClick={onReload}
                  aria-label={t["Refresh"]}
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    bgcolor: COLORS.surface.card,
                    borderRadius: `${COLORS.radius.pill}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Refresh sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )
          }
        >
          <Tooltip title={t["Home"]}>
            <IconButton
              component={Link}
              to="/"
              size="small"
              sx={{
                color: COLORS.surface.secondaryText,
                border: `1px solid ${COLORS.surface.border}`,
                borderRadius: `${COLORS.radius.pill}px`,
                bgcolor: COLORS.surface.card,
                width: 40,
                height: 40,
                flexShrink: 0
              }}
            >
              <Home sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: "1 1 160px", minWidth: 140, maxWidth: { xs: "100%", md: 220 } }}>
            <SiteSelect
              hideLabel
              value={siteId || undefined}
              onSites={setSites}
              disableAutoSelect={isSuper}
              emptyLabel={
                isSuper
                  ? t["Select a site to unlock"] || "Select a site to unlock"
                  : t["Select a site"] || "Select a site"
              }
              onChange={(id) => {
                if (!ensureBreakGlassForSite(userData?.type, id)) {
                  setPendingSiteId(id);
                  return;
                }
                applySite(id);
              }}
            />
          </Box>
          <Box sx={{ flex: "1 1 180px", minWidth: 150, maxWidth: { xs: "100%", md: 260 } }}>
            {siteId ? (
              <BoxSelect
                siteId={siteId}
                onChange={(id) => {
                  setBoxId(id);
                  if (id) localStorage.setItem("boxId", id);
                }}
                value={boxId}
                hideLabel
              />
            ) : (
              <SelectInput
                data={[{ label: t["Select a box"] || "Select a box", value: "" }]}
                value=""
                onChange={() => undefined}
                disabled
                fullWidth
                helperText=""
                displayEmpty
              />
            )}
          </Box>
          <Box sx={{ flex: "1 1 150px", minWidth: 140, maxWidth: { xs: "100%", md: 200 } }}>
            {scope.ready && enabledModels.length > 0 ? (
              <EventTypeFilter models={enabledModels} value={selectedType} onChange={setTypeFilter} />
            ) : (
              <SelectInput
                data={[{ label: t["All event types"] || "All event types", value: "all" }]}
                value="all"
                onChange={() => undefined}
                disabled
                fullWidth
                helperText=""
              />
            )}
          </Box>
          <Box sx={{ flex: "0 1 150px", minWidth: 130, maxWidth: { xs: "100%", sm: 160 } }}>
            <SelectInput
              data={PERIODS.map((elm) => ({ ...elm, label: t[elm.label] }))}
              value={period.value}
              onChange={(e) => {
                const value = Number.parseInt(e.target.value + "");
                const next = PERIODS.find((item) => item.value === value);
                if (next) setPeriod(next);
              }}
              fullWidth
              helperText=""
            />
          </Box>
        </FilterBar>
      </Box>

      {needsSuperUnlock ? <SuperBreakGlassPanel /> : null}
      {!needsSuperUnlock && scope.ready && enabledModels.length === 0 ? (
        <Box mb={2}>
          <NoDetectionModels />
        </Box>
      ) : null}

      {needsSuperUnlock ? (
        <EmptyDashboardShell />
      ) : !scope.ready ? null : enabledModels.length === 0 ? (
        <EmptyDashboardShell
          message={
            t["Dashboard no models charts hint"] ||
            "Enable detection models in site settings to populate these charts."
          }
        />
      ) : (
        <>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            mb={2}
            sx={{
              "& > *": {
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 8px)",
                  md: `1 1 calc(${100 / Math.min(chartModels.length, 3)}% - 11px)`,
                  lg: `1 1 calc(${100 / Math.min(chartModels.length, 4)}% - 12px)`,
                  xl: `1 1 calc(${100 / chartModels.length}% - ${(16 * (chartModels.length - 1)) / chartModels.length}px)`
                },
                minWidth: 0,
                maxWidth: {
                  xs: "100%",
                  sm: "calc(50% - 8px)",
                  md: `calc(${100 / Math.min(chartModels.length, 3)}% - 11px)`,
                  lg: `calc(${100 / Math.min(chartModels.length, 4)}% - 12px)`,
                  xl: `calc(${100 / chartModels.length}% - ${(16 * (chartModels.length - 1)) / chartModels.length}px)`
                }
              }
            }}
          >
            {chartModels.map((model) => (
              <Box key={model}>
                <CountChart
                  loading={loading}
                  period={t[`${period.label}`]}
                  threats={chartThreats}
                  threatsOld={responseOld?.data?.threats}
                  level={MODELS.indexOf(model)}
                />
              </Box>
            ))}
          </Box>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <Pie threats={chartThreats} period={t[`${period.label}`]} models={chartModels} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Bar threats={chartThreats} period={t[`${period.label}`]} models={chartModels} />
            </Grid>
          </Grid>
        </>
      )}

      <ThreatsTable
        boxId={boxId}
        siteId={siteId || undefined}
        title={t["Recent AI Detected Incidents"] || "Recent AI Detected Incidents"}
        hideExport
        models={enabledModels}
        modelsReady={scope.ready}
        typeFilter={selectedType}
        onTypeFilterChange={setTypeFilter}
        hideTypeFilter
        keepEmptyTableLayout
      />
    </PrivateLayout>
  );
}
